import React, { useCallback, useState, useEffect, useRef } from 'react';
import Cookie from 'js-cookie';
import _pick from 'lodash/pick';
import _pickBy from 'lodash/pickBy';
import _identity from 'lodash/identity';
import _isEqual from 'lodash/isEqual';
import store from 'store';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { formValues } from 'redux-form';

import { getUtilities, getActions as getSearchActions } from '@ott/state-cars-search';
import { useLayoutContext } from '@ott/layout-context';
import { useConfigContext } from '@ott/config-context';
import { compose } from 'lodash/fp';

import prepareRouterParams from './helpers/prepareRouterParams';
import getDomElementByName from './helpers/getDomElementByName';
import transformPoints from '../utilities/transformPoints';
import {
  PICKUP_DATE,
  DROPOFF_DATE,
  PICKUP_FIELD_NAME,
  DROPOFF_FIELD_NAME,
  LAST_SEARCH_KEY,
} from '../consts';
import useValidate from '../hooks/useValidate';
import {
  requestSuggestedPoints,
  resetPoints,
  resetSelectedPoint,
  selectPoint,
  setDirectionValue,
} from '../redux/suggest/actions';
import { CarsSearchFormPropTypes } from './types';
import CarsSearchLayout from './CarsSearchLayout';

const { getVariants } = getSearchActions();

const removeEmptyFields = (data) => _pickBy(data, _identity);

export const CarsSearchForm = React.memo((props) => {
  const {
    className,
    dispatch,
    handleSubmit,
    suggest,
    suggest: {
      pickUp: { actualValue: pickUpActualValue },
      dropOff: { actualValue: dropOffActualValue },
    },
    lang,
    [PICKUP_DATE]: pickUpDateValue,
    [DROPOFF_DATE]: dropOffDateValue,
    change,
    isTest,
    initialPickUpPoint = {},
    initialDropOffPoint = {},
    search,
    fieldNames,
    onSearch,
    formRef,
    submitRef,
  } = props;
  const { referrer } = useConfigContext();
  const { isDesktopLayout, viewportWidth } = useLayoutContext();
  const [isSameLocation, setIsSameLocation] = useState(true);
  const pickUpFieldRef = useRef();
  const pickUpDatepickerRef = useRef();
  const dropOffFieldRef = useRef();
  const dropOffDatepickerRef = useRef();
  const { isShowError, submitTime, errors, validateFields, setIsShowError } = useValidate();

  const pickUpPoints = transformPoints(_get(suggest, 'pickUp.points', []));
  const dropOffPoints = transformPoints(_get(suggest, 'dropOff.points', []));

  const toggleDropOffVisibilityState = useCallback(() => {
    setIsSameLocation((prevState) => !prevState);
  }, [isSameLocation, setIsSameLocation]);

  useEffect(() => {
    /**
     * FIX:
     * Нужно будет переписать, когда в копонентах будет реализован forwardRef
     * На момент написания querySelector единственный возможный способ вызвать фокус у вложенных компонентовif (isSamePlace
     */

    pickUpFieldRef.current = getDomElementByName(PICKUP_FIELD_NAME);
    pickUpDatepickerRef.current = getDomElementByName(PICKUP_DATE);
    dropOffFieldRef.current = getDomElementByName(DROPOFF_FIELD_NAME);
    dropOffDatepickerRef.current = getDomElementByName(DROPOFF_DATE);

    if (!_isEmpty(initialPickUpPoint)) {
      dispatch(selectPoint(PICKUP_FIELD_NAME, initialPickUpPoint));
      dispatch(setDirectionValue(PICKUP_FIELD_NAME, initialPickUpPoint));
    }

    if (!_isEmpty(initialDropOffPoint)) {
      dispatch(selectPoint(DROPOFF_FIELD_NAME, initialDropOffPoint));
      dispatch(setDirectionValue(DROPOFF_FIELD_NAME, initialDropOffPoint));
    }

    if (!_isEmpty(initialDropOffPoint)) {
      toggleDropOffVisibilityState();
    }
  }, []);

  // NOTE: Если места получения и возврата совпадают, то очищаем localStorage и значение в store.
  useEffect(() => {
    if (
      !_isEmpty(initialPickUpPoint) &&
      !_isEmpty(initialDropOffPoint) &&
      _isEqual(initialPickUpPoint, initialDropOffPoint)
    ) {
      const lastSearchData = store.get(LAST_SEARCH_KEY);
      store.set(LAST_SEARCH_KEY, {
        ...lastSearchData,
        [DROPOFF_FIELD_NAME]: {},
      });
      dispatch(resetSelectedPoint(DROPOFF_FIELD_NAME));
      dispatch(setDirectionValue(DROPOFF_FIELD_NAME, {}));
      toggleDropOffVisibilityState();
    }
  }, []);

  const handleLocationTypeChange = useCallback(() => {
    toggleDropOffVisibilityState();
    dispatch(setDirectionValue(DROPOFF_FIELD_NAME, {}));
    dispatch(resetSelectedPoint(DROPOFF_FIELD_NAME));
    change(DROPOFF_FIELD_NAME, '');
  }, [dispatch, change, toggleDropOffVisibilityState, setDirectionValue, resetSelectedPoint]);

  const handleChangeDirection = useCallback(
    (directionType) =>
      (value = '') => {
        if (!value.length) {
          dispatch(setDirectionValue(directionType, {}));
          dispatch(resetPoints(directionType));
        }

        if (value.length >= 3) {
          dispatch(requestSuggestedPoints({ directionType, term: value, lang, isTest }));
        }
      },
    [
      lang,
      isTest,
      pickUpActualValue,
      dropOffActualValue,
      suggest,
      setDirectionValue,
      resetPoints,
      requestSuggestedPoints,
    ]
  );

  const handleSelectPoint = useCallback(
    (direction) =>
      (selectedItem, { selectNext = false }) => {
        const isPickUpField = direction === PICKUP_FIELD_NAME;
        const isDropOffField = direction === DROPOFF_FIELD_NAME;

        if (selectedItem) {
          setIsShowError(false);
          dispatch(selectPoint(direction, selectedItem));
          dispatch(setDirectionValue(direction, selectedItem));
        } else {
          dispatch(setDirectionValue(direction, {}));
          change(direction, '');
        }

        if (isPickUpField && selectedItem && selectNext) {
          // После обновления реакта до 17 версии handleClickOutside в компонентах Travellers и Datepicker перестал корректно работать:
          // клик при выборе значения предыдущего поля инициализирует фокус на следующем поле и последующую установку хендлера handleClickOutside,
          // при этом сам клик успевает отловиться этим же хендлером, что вызывает закрытие оверлея у Travellers или Datepicker.
          // Поэтому приходится обернуть фокусировку в setTimeOut
          setTimeout(() => pickUpDatepickerRef.current.focus());
        }

        if (isDropOffField && selectedItem && selectNext) {
          setTimeout(() => dropOffDatepickerRef.current.focus());
        }
      },
    [
      dropOffDatepickerRef,
      pickUpDatepickerRef,
      dispatch,
      change,
      setIsShowError,
      selectPoint,
      setDirectionValue,
    ]
  );

  const handleDataSelect = useCallback(
    (fieldName) => (data) => {
      const isMoreThanTablet = viewportWidth > 768;
      change(fieldName, { startDate: data });

      if (fieldName === PICKUP_DATE && data > dropOffDateValue.startDate) {
        change(DROPOFF_DATE, { startDate: data });
      }

      if (fieldName === PICKUP_DATE && isMoreThanTablet) {
        setTimeout(() => dropOffDatepickerRef.current.focus());
      }
    },
    [viewportWidth, dropOffDateValue, dropOffDatepickerRef, change]
  );

  const handleSubmitCarsSearchForm = useCallback(
    (values) => {
      const {
        pickUpDate: { startDate: valuePickUpDate } = {},
        dropOffDate: { startDate: valueDropOffDate } = {},
        ...restFormValues
      } = values;

      const actualValuesForValidateFields = _pick(values, fieldNames);

      const isSamePlace =
        !_isEmpty(dropOffActualValue) && _isEqual(pickUpActualValue, dropOffActualValue);

      const validDropOff = isSamePlace || _isEmpty(dropOffActualValue) ? null : dropOffActualValue;

      if (isSamePlace) {
        handleLocationTypeChange();
      }

      const hasError = validateFields(actualValuesForValidateFields);
      if (hasError) {
        return;
      }

      const valuesForSubmit = removeEmptyFields({
        ...restFormValues,
        pickUpDate: valuePickUpDate,
        dropOffDate: valueDropOffDate,
      });

      const data = {
        ...valuesForSubmit,
        resellerId: referrer || Cookie.get('referrer') || '12trip',
        isTest,
      };
      const validData = removeEmptyFields(data);

      const { prepareSearchParams } = getUtilities();

      dispatch(getVariants(validData));

      const searchParams = prepareSearchParams({ suggest, search }, data);

      const { pKey, dKey } = searchParams;
      const isOneDirection = isSamePlace || isSameLocation; // FIX: нужно будет придумать названия оригинальней
      const validDKey = isOneDirection ? '' : dKey;

      const validFormValues = removeEmptyFields({
        ...actualValuesForValidateFields,
        pickUp: pickUpActualValue,
        dropOff: validDropOff,
        pKey,
        dKey: validDKey,
      });

      store.set(LAST_SEARCH_KEY, { ...validFormValues });

      const routerParams = prepareRouterParams(searchParams, {
        pickUp: pickUpActualValue,
        dropOff: validDropOff,
      });

      const validRouterParams = removeEmptyFields(routerParams);

      if (onSearch) {
        onSearch(validRouterParams, validFormValues);
      }
    },
    [pickUpActualValue, dropOffActualValue, search, suggest, isSameLocation, fieldNames, onSearch]
  );

  const valueForAutocomplete = React.useCallback((value) => (_isEmpty(value) ? null : value), []);

  return (
    <CarsSearchLayout
      formRef={formRef}
      submitRef={submitRef}
      handleLocationTypeChange={handleLocationTypeChange}
      isSameLocation={isSameLocation}
      className={className}
      handleSubmitCarsSearchForm={handleSubmitCarsSearchForm}
      errors={errors}
      isShowError={isShowError}
      submitTime={submitTime}
      handleSubmit={handleSubmit}
      pickUpPoints={pickUpPoints}
      handleChangeDirection={handleChangeDirection}
      valueForAutocomplete={valueForAutocomplete}
      pickUpActualValue={pickUpActualValue}
      handleSelectPoint={handleSelectPoint}
      dropOffPoints={dropOffPoints}
      dropOffActualValue={dropOffActualValue}
      handleDataSelect={handleDataSelect}
      isDesktopLayout={isDesktopLayout}
      pickUpDateValue={pickUpDateValue}
      change={change}
    />
  );
});

CarsSearchForm.displayName = 'CarsSearchForm';
CarsSearchForm.propTypes = {
  ...CarsSearchFormPropTypes,
};

export default compose(
  formValues({
    [PICKUP_DATE]: PICKUP_DATE,
    [DROPOFF_DATE]: DROPOFF_DATE,
  })
  // Попытка совместить 2 flow взаимодействия с формой по enter
  // withEnterSubmit
)(CarsSearchForm);
